import PropTypes from 'prop-types'
import {useFormContext, Controller} from 'react-hook-form'
import {useState, useEffect} from 'react'
import {Box, FormControl, TextField, Autocomplete, FormHelperText, Typography} from '@mui/material'
import {LocationOn} from '@mui/icons-material'
import {NAVY, SUBTLE, BORDER, PRIMARY_BLUE} from '../../styles/foundation/colors'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'

RHFPincodeAddressField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  fetchAddresses: PropTypes.func.isRequired, // API function to fetch addresses
  onAddressSelect: PropTypes.func.isRequired, // Callback to update other fields
  debounceMs: PropTypes.number,
}

export default function RHFPincodeAddressField({
  name,
  label = 'Pincode',
  variant = 'outlined',
  fetchAddresses,
  onAddressSelect,
  debounceMs = 500,
  ...props
}) {
  const {control, watch} = useFormContext()
  const [addresses, setAddresses] = useState([])
  const [loading, setLoading] = useState(false)
  const pincode = watch(name)

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (pincode && pincode.length >= 6) {
        setLoading(true)
        try {
          const result = await fetchAddresses(pincode)
          setAddresses(result)
        } catch (error) {
          console.error('Error fetching addresses:', error)
          setAddresses([])
        } finally {
          setLoading(false)
        }
      } else {
        setAddresses([])
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [pincode, fetchAddresses, debounceMs])

  return (
    <Controller
      name={name}
      control={control}
      render={({field, fieldState: {error}}) => (
        <FormControl error={!!error} fullWidth>
          <Autocomplete
            size='small'
            options={addresses || []}
            loading={loading}
            freeSolo
            loadingText={
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                Loading...
              </Typography>
            }
            noOptionsText={
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                {pincode && pincode.length >= 6 ? 'No addresses found' : 'Type 6-digit pincode'}
              </Typography>
            }
            slotProps={{
              paper: {
                sx: {
                  borderRadius: '10px',
                  border: `1px solid ${BORDER}`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  mt: 0.5,
                  '& .MuiAutocomplete-listbox': {
                    py: 0.5,
                  },
                },
              },
            }}
            onChange={(_, newValue) => {
              field.onChange(newValue?.pincode || newValue)
              if (newValue && typeof newValue === 'object') {
                onAddressSelect(newValue)
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                {...field}
                label={label}
                variant={variant}
                size='small'
                error={!!error}
                onChange={(e) => field.onChange(e.target.value)}
                inputProps={{
                  ...params.inputProps,
                  maxLength: 6,
                }}
                InputProps={{
                  ...params.InputProps,
                  sx: { fontFamily: FONT, fontSize: SIZE['2xs'] },
                }}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  sx: {
                    fontFamily: FONT,
                    fontSize: 10,
                    '&:not(.MuiInputLabel-shrink)': {
                      transform: 'translate(14px, 12px) scale(1)',
                    },
                  },
                }}
                sx={{
                  // Value alignment only: the filled Autocomplete vertically-centres its small
                  // input, so the selected pincode hugs the top of the box. Move the padding onto
                  // the input so the value sits lower/centred like the plain filled fields
                  // (City/State) next to it. No colour/background changes.
                  '& .MuiFilledInput-root': { paddingTop: '0 !important', paddingBottom: '0 !important' },
                  '& .MuiFilledInput-root .MuiAutocomplete-input': { padding: '19px 12px 4px 12px !important' },
                }}
                {...props}
              />
            )}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option
              const addressDetails = [option.city, option.state, option.country]
                .filter(Boolean)
                .join(', ')
              return addressDetails ? `${option.pincode} - ${addressDetails}` : option.pincode
            }}
            renderOption={(renderProps, option) => {
              const addressDetails = [option.city, option.state, option.country]
                .filter(Boolean)
                .join(', ')
              return (
                <li {...renderProps}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                    <LocationOn sx={{ fontSize: 16, color: PRIMARY_BLUE, flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY }}>
                        {option.pincode}
                      </Typography>
                      {addressDetails && (
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.68rem', color: SUBTLE }}>
                          {addressDetails}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </li>
              )
            }}
            value={field.value}
            isOptionEqualToValue={(option, value) =>
              option.pincode === value || option.pincode === value?.pincode
            }
            inputValue={typeof field.value === 'string' ? field.value : field.value?.pincode || ''}
          />
          <FormHelperText>{error?.message}</FormHelperText>
        </FormControl>
      )}
    />
  )
}
